/**
 * Addresses and transaction hashes, linked to the chain's block explorer when it has one. The
 * local chain has none, so there they are shown as text. Every address has a button that copies
 * it, so anyone can paste it into a wallet or an explorer.
 */

import { explorerAddressUrl, explorerTransactionUrl } from "../wallet/wagmi.js";
import { CopyButton } from "./ui.js";

/** "0x5e5e5e5e…5e5e5e5e": enough of a hash or address to recognise it. */
export function shortHex(value: string): string {
  return `${value.slice(0, 10)}…${value.slice(-8)}`;
}

/**
 * An address linked to its explorer page, with a button that copies it. In full by default, or
 * `short` for tables, where the full address is its title.
 */
export function AddressLink({
  chainId,
  address,
  short = false,
}: {
  chainId: number;
  address: string;
  short?: boolean;
}) {
  const url = explorerAddressUrl(chainId, address);
  const text = short ? shortHex(address) : address;
  return (
    <span className="inline">
      {url ? (
        <a href={url} title={address} className="font-mono text-xs break-all hover:underline">
          {text}
        </a>
      ) : (
        <span title={address} className="font-mono text-xs break-all">
          {text}
        </span>
      )}{" "}
      <CopyButton value={address} label="Copy address" compact />
    </span>
  );
}

/** A transaction hash, shortened, linked to its explorer page. The full hash is its title. */
export function TransactionLink({ chainId, hash }: { chainId: number; hash: string }) {
  const url = explorerTransactionUrl(chainId, hash);
  return url ? (
    <a href={url} className="font-mono text-xs hover:underline" title={hash}>
      {shortHex(hash)}
    </a>
  ) : (
    <span className="font-mono text-xs" title={hash}>
      {shortHex(hash)}
    </span>
  );
}
